import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "@/store/slices/cartSlice";
import { addToWishlist, removeFromWishlist } from "@/store/slices/uiSlice";
import Button from "@mui/material/Button";
import Rating from "@mui/material/Rating";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { useProduct, useRelatedProducts } from "@/hooks/useProducts";
import ImageWithFallback from "@/components/ImageWithFallback";
import LoadingScreen from "@/components/LoadingScreen";
import ShareIcon from "@mui/icons-material/Share";
import ShareModal from "@/components/ShareModal";
import Box from "@mui/material/Box";
import ReviewSystem from "@/components/ReviewSystem";

const PDP = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const wishlist = useSelector((s) => s.ui.wishlist);
  const [tab, setTab] = React.useState(0);
  const [activeImage, setActiveImage] = React.useState(0);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = React.useState(false);
  const [imageErrorMap, setImageErrorMap] = React.useState({});
  const [selectedVariant, setSelectedVariant] = React.useState(null);
  const [reviewHeaderMeta, setReviewHeaderMeta] = React.useState(null);

  const onReviewMetaChange = React.useCallback((meta) => {
    if (meta && meta.avgRating != null && meta.reviewCount != null) {
      setReviewHeaderMeta({
        avgRating: Number(meta.avgRating),
        reviewCount: Number(meta.reviewCount),
      });
    }
  }, []);

  React.useEffect(() => {
    setReviewHeaderMeta(null);
  }, [id]);

  // API hooks
  const { product, loading, error } = useProduct(id);
  const { relatedProducts: related, loading: relatedLoading } =
    useRelatedProducts(product);

  // Set selected variant when product loads
  React.useEffect(() => {
    if (product && product.variants && product.variants.length > 0) {
      setSelectedVariant(product.variants[0]);
    }
  }, [product]);

  const isWishlisted = product && wishlist.includes(product._id);

  React.useEffect(() => {
    setActiveImage(0);
    setTab(0);
    setImageErrorMap({});
  }, [id]);

  const toggleWishlist = () => {
    if (isWishlisted) dispatch(removeFromWishlist(product._id));
    else dispatch(addToWishlist(product._id));
  };

  const handleMainImageClick = () => {
    if (!imageErrorMap[activeImage]) {
      setIsModalOpen(true);
    }
  };

  const markImageError = (index) => () =>
    setImageErrorMap((m) => ({ ...m, [index]: true }));

  if (loading) return <LoadingScreen />;
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
        <div className="text-left max-w-md mx-auto">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Failed to load product
          </h2>
          <p className="text-slate-600 mb-6">
            {error.response?.status === 404
              ? "This product doesn't exist or has been removed."
              : "We're having trouble loading the product details. Please try again."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
            <Link
              to="/shop"
              className="bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300 transition-colors text-center"
            >
              Browse Products
            </Link>
          </div>
        </div>
      </div>
    );
  }
  if (!product) return <div>Product not found</div>;

  const handleVariantChange = (variant) => {
    setSelectedVariant(variant);
  };

  const displayAvgRating =
    reviewHeaderMeta != null
      ? Number(reviewHeaderMeta.avgRating) || 0
      : Number(product.avgRating) || 0;
  const displayReviewCount =
    reviewHeaderMeta != null
      ? Number(reviewHeaderMeta.reviewCount) || 0
      : Number(product.reviewCount) || 0;

  return (
    <div className="bg-white text-slate-900 min-h-screen">
      <div className="container max-w-6xl mx-auto py-4 sm:py-6 lg:py-10 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
          {/* Gallery */}
          <div className="flex flex-col gap-3 sm:gap-4">
            <div
              className={`relative w-full aspect-[4/3] rounded-lg sm:rounded-xl lg:rounded-2xl overflow-hidden bg-white shadow ${
                imageErrorMap[activeImage] ? "cursor-default" : "cursor-zoom-in"
              }`}
              onClick={handleMainImageClick}
            >
              <ImageWithFallback
                src={product.images?.[activeImage]}
                alt={product.name}
                className="w-full h-full object-cover"
                onImageError={markImageError(activeImage)}
              />
            </div>

            {/* Thumbnail grid - mobile optimized */}
            <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {product.images?.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`flex-shrink-0 h-16 w-16 sm:h-20 sm:w-20 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                    activeImage === i
                      ? "border-slate-900 ring-2 ring-slate-900/20"
                      : "border-slate-200 hover:border-slate-400"
                  }`}
                >
                  <ImageWithFallback
                    src={src}
                    alt={`${product.name} ${i + 1}`}
                    className="w-full h-full object-cover"
                    onImageError={markImageError(i)}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Info - Left aligned for mobile */}
          <div className="lg:sticky lg:top-24 h-max space-y-4 sm:space-y-6">
            {/* Header with wishlist button - left aligned */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight text-left">
                  {product.name}
                </h1>
              </div>
              <Box className="flex gap-1 sm:gap-2 shrink-0">
                {/* Wishlist Action */}
                <Box sx={{ display: { xs: "flex", sm: "none" } }}>
                  <IconButton
                    onClick={toggleWishlist}
                    color={isWishlisted ? "error" : "default"}
                    size="small"
                  >
                    {isWishlisted ? (
                      <FavoriteIcon fontSize="small" />
                    ) : (
                      <FavoriteBorderIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
                <Box sx={{ display: { xs: "none", sm: "flex" } }}>
                  <Button
                    onClick={toggleWishlist}
                    startIcon={
                      isWishlisted ? <FavoriteIcon /> : <FavoriteBorderIcon />
                    }
                    color={isWishlisted ? "error" : "inherit"}
                    size="small"
                  >
                    {isWishlisted ? "Wishlisted" : "Wishlist"}
                  </Button>
                </Box>

                {/* Share Action */}
                <Box sx={{ display: { xs: "flex", sm: "none" } }}>
                  <IconButton
                    onClick={() => setIsShareModalOpen(true)}
                    size="small"
                  >
                    <ShareIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ display: { xs: "none", sm: "flex" } }}>
                  <Button
                    onClick={() => setIsShareModalOpen(true)}
                    startIcon={<ShareIcon />}
                    color="inherit"
                    size="small"
                  >
                    Share
                  </Button>
                </Box>
              </Box>
            </div>

            {/* Rating Summary */}
            <div className="flex items-center gap-3">
              <Rating 
                value={displayAvgRating} 
                precision={0.1} 
                readOnly 
                size="small" 
                sx={{ color: '#fbbf24' }}
              />
              <span className="text-sm font-semibold text-slate-500 hover:text-slate-900 cursor-pointer transition-colors">
                {(displayAvgRating || 0).toFixed(1)} ({displayReviewCount} reviews)
              </span>
            </div>

            {/* Price - left aligned */}
            <div className="text-left">
              <div className="flex items-baseline gap-3">
                {product.discount > 0 ? (
                  <>
                    <span className="text-slate-900 text-2xl sm:text-3xl font-bold">
                      ₹{( (selectedVariant ? selectedVariant.price : product.price) * (1 - product.discount / 100) ).toFixed(0)}
                    </span>
                    <span className="text-slate-400 text-lg line-through font-medium">
                      ₹{selectedVariant ? selectedVariant.price : product.price}
                    </span>
                    <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded">
                      {product.discount}% OFF
                    </span>
                  </>
                ) : (
                  <span className="text-slate-900 text-2xl sm:text-3xl font-bold">
                    ₹{selectedVariant ? selectedVariant.price : product.price}
                  </span>
                )}
              </div>
            </div>

            {/* Stock Label - left aligned */}
            <div className="text-left">
              {selectedVariant ? (
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedVariant.stock > 0
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {selectedVariant.stock > 0
                    ? `${selectedVariant.stock} in stock`
                    : "Out of Stock"}
                </span>
              ) : (
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    product.stock > 0
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {product.stock > 0
                    ? `${product.stock} in stock`
                    : "Out of Stock"}
                </span>
              )}
            </div>

            {/* Product descriptions - left aligned */}
            {product.subheading && (
              <div className="text-left">
                <p className="text-slate-600 text-base sm:text-lg font-medium">
                  {product.subheading}
                </p>
              </div>
            )}

            {product.description && (
              <div className="text-left">
                <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                  {product.description}
                </p>
              </div>
            )}

            {/* Variant Selection - left aligned */}
            {product.variants && product.variants.length > 0 && (
              <div className="space-y-4">
                <div className="text-left">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">
                    Color
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {[
                      ...new Set(
                        product.variants.map((v) => v.attributes.color),
                      ),
                    ].map((color) => (
                      <Button
                        key={color}
                        variant={
                          selectedVariant.attributes.color === color
                            ? "contained"
                            : "outlined"
                        }
                        size="small"
                        onClick={() =>
                          handleVariantChange(
                            product.variants.find(
                              (v) => v.attributes.color === color,
                            ),
                          )
                        }
                        className="min-w-0"
                      >
                        {color}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="text-left">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">
                    Size
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {[
                      ...new Set(
                        product.variants.map((v) => v.attributes.size),
                      ),
                    ].map((size) => (
                      <Button
                        key={size}
                        variant={
                          selectedVariant.attributes.size === size
                            ? "contained"
                            : "outlined"
                        }
                        size="small"
                        onClick={() =>
                          handleVariantChange(
                            product.variants.find(
                              (v) => v.attributes.size === size,
                            ),
                          )
                        }
                        className="min-w-0"
                      >
                        {size}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Action buttons - full width on mobile */}
            <div className="space-y-3 sm:space-y-0 sm:flex sm:gap-3">
              {!((selectedVariant && selectedVariant.stock <= 0) || (!selectedVariant && product.stock <= 0)) && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  fullWidth
                  className="sm:w-auto py-3"
                  onClick={() =>
                    dispatch(
                      addToCart({
                        productId: product._id,
                        name: product.name,
                        price: product.discount > 0 
                          ? Math.round((selectedVariant ? selectedVariant.price : product.price) * (1 - product.discount / 100))
                          : (selectedVariant ? selectedVariant.price : product.price),
                        image: product.images?.[0],
                        variant: selectedVariant ? selectedVariant.sku : null,
                        stock: selectedVariant
                          ? selectedVariant.stock
                          : product.stock,
                      }),
                    )
                  }
                >
                  Add to Cart
                </Button>
              )}
              {((selectedVariant && selectedVariant.stock <= 0) || (!selectedVariant && product.stock <= 0)) ? (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  className="py-3"
                  onClick={() => {
                    dispatch(
                      addToCart({
                        productId: product._id,
                        name: product.name,
                        price: product.discount > 0 
                          ? Math.round((selectedVariant ? selectedVariant.price : product.price) * (1 - product.discount / 100))
                          : (selectedVariant ? selectedVariant.price : product.price),
                        image: product.images?.[0],
                        variant: selectedVariant ? selectedVariant.sku : null,
                        stock: 999, // Allow checkout to proceed
                        isPreorder: true,
                      }),
                    );
                    navigate("/checkout");
                  }}
                >
                  Pre Order
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  className="py-3"
                  onClick={() => {
                    dispatch(
                      addToCart({
                        productId: product._id,
                        name: product.name,
                        price: product.discount > 0 
                          ? Math.round((selectedVariant ? selectedVariant.price : product.price) * (1 - product.discount / 100))
                          : (selectedVariant ? selectedVariant.price : product.price),
                        image: product.images?.[0],
                        variant: selectedVariant ? selectedVariant.sku : null,
                        stock: selectedVariant
                          ? selectedVariant.stock
                          : product.stock,
                      }),
                    );
                    navigate("/checkout");
                  }}
                >
                  Buy Now
                </Button>
              )}
            </div>

            {/* Product details - left aligned */}
            <div className="space-y-6">
              <div className="bg-slate-50 rounded-xl p-4 sm:p-6">
                {product.description && (
                  <div className="text-left">
                    <h2 className="font-display text-lg sm:text-xl mb-3 text-slate-900">
                      Product Details
                    </h2>
                    <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                      {product.description}
                    </p>
                  </div>
                )}
                {product.specifications &&
                  product.specifications.length > 0 && (
                    <div className="text-left mt-6">
                      <h2 className="font-display text-lg sm:text-xl mb-3 text-slate-900">
                        Specifications
                      </h2>
                      <div className="space-y-2">
                        {product.specifications.map((spec, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-start py-2 border-b border-slate-200 last:border-0"
                          >
                            <span className="text-sm font-medium text-slate-600">
                              {spec.key}
                            </span>
                            <span className="text-sm text-slate-900 text-right ml-4">
                              {spec.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </div>

          </div>
        </div>
        {/* Review System */}
        <ReviewSystem productId={product._id} onReviewMetaChange={onReviewMetaChange} />
      </div>

      {/* Related products */}
      {related?.length > 0 && (
        <section className="container max-w-6xl mx-auto py-6 sm:py-8 lg:py-10 px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-xl sm:text-2xl mb-4 sm:mb-6 text-left">
            You might also like
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            {related.map((p) => (
              <Link
                to={`/product/${p._id}`}
                key={p._id}
                className="bg-white rounded-xl p-3 shadow hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
              >
                <div className="relative aspect-square overflow-hidden rounded-lg mb-3">
                  <ImageWithFallback
                    src={p.images?.[0]}
                    alt={p.name}
                    className="w-full h-full object-cover hover:scale-105 transition duration-500"
                  />
                </div>
                {p.name && (
                  <div className="text-left">
                    <div className="text-slate-700 text-sm line-clamp-2 mb-1">
                      {p.name}
                    </div>
                    <div className="flex items-center gap-2">
                      {p.discount > 0 ? (
                        <>
                          <span className="text-slate-900 font-bold">₹{(p.price * (1 - p.discount / 100)).toFixed(0)}</span>
                          <span className="text-xs text-slate-400 line-through">₹{p.price}</span>
                        </>
                      ) : (
                        <span className="text-slate-900 font-semibold text-base">₹{p.price}</span>
                      )}
                    </div>
                  </div>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Fullscreen image modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        fullWidth
        maxWidth="lg"
        className="lg:p-4"
        PaperProps={{
          className: "m-2 sm:m-4 lg:m-8 max-h-[95vh] lg:max-h-[90vh]",
        }}
      >
        <div className="relative bg-white rounded-xl sm:rounded-2xl overflow-hidden">
          <div className="absolute top-2 right-2 sm:top-3 sm:right-3 z-20">
            <IconButton
              onClick={() => setIsModalOpen(false)}
              className="bg-white/90 hover:bg-white shadow-lg backdrop-blur-sm"
              size="small"
            >
              <CloseIcon className="text-slate-700" />
            </IconButton>
          </div>

          <div className="relative bg-slate-50">
            <div className="absolute inset-0 bg-gradient-to-b from-black/5 to-transparent pointer-events-none" />
            <ImageWithFallback
              src={product.images?.[activeImage]}
              alt={`Full ${product.name}`}
              className="w-full h-auto max-h-[60vh] sm:max-h-[70vh] lg:max-h-[75vh] object-contain"
            />
          </div>

          <div className="p-3 sm:p-4 bg-white/95 backdrop-blur sticky bottom-0 border-t border-slate-100">
            <div className="flex gap-2 overflow-x-auto scrollbar-hide">
              {product.images?.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`flex-shrink-0 h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 rounded-md sm:rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                    activeImage === i
                      ? "border-slate-900 ring-2 ring-slate-900/20"
                      : "border-slate-200 hover:border-slate-400"
                  }`}
                >
                  <ImageWithFallback
                    src={src}
                    alt={`${product.name} ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </Dialog>

      <ShareModal
        open={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        url={window.location.href}
        title={product.name}
      />
    </div>
  );
};

export default PDP;
